import React, { useMemo, useState, useEffect, useCallback, useRef, useLayoutEffect } from "react"
import InputText from "../inputText/inputText"
import Select from "../select/select"
import {
    HotelRoomPricingModel,
    HotelContractModel,
    HotelRoomTypeModel,
    MealPlanModel,
    RoomSpecificDifferentSurchargeModel,
    SeasonalExtraBedPricingModel,
} from "../../models"
import styles from "./hotelRates.module.scss"
import { agent } from "../../apiClient/api"
import { useSnackbar } from "../../context/snackbarContext"
import moment from "moment"
import { Grid } from "@mui/material"
import RoomSpecificPeakDateSurcharges from "./roomSpecificPeakDateSurcharges"
import PerPersonSeasonalSurcharges from "./perPersonSeasonalSurcharges"
import ThirdPersonSurchargeConfiguration from "./thirdPersonSurchargeConfiguration"
import ContractSeasonDatesBar from "./ContractSeasonDatesBar"
import { useHotelDayWisePricingContext } from "../../context/HotelDayWisePricingContext"
import {
    dayWisePricingApiToLocal,
    localDayWiseGroupsToApi,
    localDayWiseGroupsToRoomPricingDayWisePatch,
} from "../hotelContract/dayWisePricingMaps"
import type { DayWisePricingGroupLocal } from "../hotelContract/hotelContractTypes"
import { toTitleCase } from "util/textcase"

// Rate Structure Types
enum RateStructureType {
    SIMPLE = 1,
    MEAL_PLAN = 2,
    GIT_FIT = 3,
}

// GIT/FIT Type IDs
export enum GitFitType {
    FIT = 2,
    GIT = 1,
}

type Props = {
    hotelId: string
    contracts?: HotelContractModel[]
    hotelRoomTypes?: HotelRoomTypeModel[]
    /** Same scope id as HotelContract for the selected rates contract. */
    dayWisePricingScopeId?: string | null
    selectedContractId?: string | null
    onContractChange?: (contractId: string) => void
    onChange?: (prices: HotelRoomPricingModel[]) => void
    onFullChange?: (contractId: string, updates: Partial<HotelContractModel>) => void
    readOnly?: boolean
    showValidationErrors?: boolean
    onValidationError?: (hasErrors: boolean) => void
    onSaveRef?: (saveFunc: () => Promise<boolean>) => void
    onSaveSuccess?: () => void
}

// Column definition for dynamic table
interface TableColumn {
    id: string
    label: string
    type: "season" | "day_group"
    seasonId?: string
    dayGroupId?: string
    mealPlanTypeId?: number
    gitFitTypeId?: number
}

// Price data structure for cells
interface PriceData {
    priceId: string | null
    price: number | null
    roomTypeId: string
    seasonId?: string
    dayGroupId?: string
    mealPlanTypeId?: number
    gitFitTypeId?: number
}

interface RoomTypeRow {
    roomTypeId: string
    roomTypeName: string
    bedTypeName: string
    prices: { [columnId: string]: PriceData }
}

// Store full day group pricing data including IDs
interface DayGroupPricingData {
    id: string | null
    price: number | null
}

interface MealPlanRowLocal {
    meal_plan_id?: string | null
    meal_plan_name: string
    adult_charge: number | null
    child_charge: number | null
}

const emptyMealPlanRow = (): MealPlanRowLocal => ({
    meal_plan_id: null,
    meal_plan_name: "",
    adult_charge: null,
    child_charge: null,
})

const toPositiveNumberOrNull = (v: string): number | null => {
    const trimmed = v.trim()
    if (!trimmed) return null
    const n = parseFloat(trimmed)
    return Number.isFinite(n) && n > -1 ? n : null
}

const toTrimmedOrNull = (v: string): string | null => {
    const trimmed = v.trim()
    return trimmed.length ? trimmed : null
}

const toPositiveNumberFromUnknownOrNull = (v: unknown): number | null => {
    if (v === null || v === undefined || v === "") return null
    const n = typeof v === "number" ? v : Number(v)
    return Number.isFinite(n) && n >= 0 ? n : null
}

// Consistent key for day group pricing (API may use number/string IDs)
const buildDayGroupPriceKey = (
    roomTypeId: string | number | null | undefined,
    dayGroupId: string | number | null | undefined,
    mealPlanTypeId?: number | null,
    gitFitTypeId?: number | null
): string => {
    const r = roomTypeId != null && roomTypeId !== "" ? String(roomTypeId) : ""
    const d = dayGroupId != null && dayGroupId !== "" ? String(dayGroupId) : ""
    const m = mealPlanTypeId != null ? String(mealPlanTypeId) : ""
    const g = gitFitTypeId != null ? String(gitFitTypeId) : ""
    return `${r}-${d}-${m}-${g}`
}

interface PricingScope {
    mealPlanTypeId?: number
    gitFitTypeId?: number
    seasonColumns: TableColumn[]
    dayGroupColumns: TableColumn[]
}

const HotelRates: React.FC<Props> = ({
    hotelId,
    contracts = [],
    hotelRoomTypes = [],
    dayWisePricingScopeId = null,
    selectedContractId,
    onContractChange,
    onChange,
    onFullChange,
    readOnly = false,
    showValidationErrors = false,
    onValidationError,
    onSaveRef,
    onSaveSuccess,
}) => {
    const { showError, showSuccess } = useSnackbar()
    const { slices, setSlice, patchSlice } = useHotelDayWisePricingContext()
    const [localPrices, setLocalPrices] = useState<HotelRoomPricingModel[]>([])
    const [dayGroupPrices, setDayGroupPrices] = useState<{ [key: string]: DayGroupPricingData }>({})
    const [extraBedRates, setExtraBedRates] = useState<{ [seasonId: string]: number | null }>({})
    const [extraBedRateIsPercentage, setExtraBedRateIsPercentage] = useState<{ [seasonId: string]: boolean }>({})
    const [extraBedPrice, setExtraBedPrice] = useState<number | null>(null)
    const [childWithoutBedPrice, setChildWithoutBedPrice] = useState<number | null>(null)
    const [childWithBed, setChildWithBed] = useState<number | null>(null)
    const [adultExtraBedPrice, setAdultExtraBedPrice] = useState<number | null>(null)
    const [surchargePerAdditionalPax, setSurchargePerAdditionalPax] = useState<number | null>(null)
    const [roomSpecificDifferentSurcharge, setRoomSpecificDifferentSurcharge] = useState<RoomSpecificDifferentSurchargeModel[]>([])
    const [mealPlans, setMealPlans] = useState<MealPlanRowLocal[]>([])
    const mealPlansDirtyRef = useRef(false)
    const mealPlansLastContractIdRef = useRef<string | null>(null)
    const dayGroupPricesDirtyRef = useRef(false)
    const dayGroupPricesLastContractIdRef = useRef<string | null>(null)
    const [isSaving, setIsSaving] = useState(false)
    const [localValidationErrors, setLocalValidationErrors] = useState(false)

    // Get the selected contract
    const selectedContract = useMemo(() => {
        if (!selectedContractId || contracts.length === 0) return contracts[0] || null
        return contracts.find((c) => c.contract_id === selectedContractId) || contracts[0] || null
    }, [contracts, selectedContractId])

    useLayoutEffect(() => {
        if (!dayWisePricingScopeId || !selectedContract) return
        patchSlice(dayWisePricingScopeId, (prev) =>
            prev ?? {
                flag_day_wise_pricing: !!selectedContract.flag_day_wise_pricing,
                dayWiseGroups: dayWisePricingApiToLocal(selectedContract.day_wise_pricing),
            },
        )
    }, [dayWisePricingScopeId, selectedContract?.contract_id, patchSlice, selectedContract])

    const dayWiseSurchargeRows = useMemo(() => {
        const slice = dayWisePricingScopeId ? slices[dayWisePricingScopeId] : undefined
        const flagOn = slice?.flag_day_wise_pricing ?? selectedContract?.flag_day_wise_pricing
        if (!flagOn) return [] as {
            rowKey: string
            label: string
            amount: number | null
            isPercentage?: boolean
            groupId: string | null
            dayWisePricingGroupSurcharge: {
                flag_different_surcharge: boolean
                surcharge_amount: number | null
                flag_surcharge_amount_is_percentage?: boolean
            }[]
        }[]
        const groups = slice?.dayWiseGroups ?? dayWisePricingApiToLocal(selectedContract?.day_wise_pricing)
        return groups
            .filter((g) => g.flag_surchange_applied)
            .sort(
                (a, b) =>
                    (a.sequence - b.sequence) ||
                    String(a.day_wise_pricing_group_id).localeCompare(String(b.day_wise_pricing_group_id)),
            )
            .map((g) => ({
                rowKey: String(g.day_wise_pricing_group_id),
                label: (g.day_wise_pricing_group_name || "").trim() || "Day-wise group",
                amount: g.surcharge_amount,
                isPercentage: g.flag_surcharge_amount_is_percentage === true,
                groupId: g.day_wise_pricing_group_id,
                dayWisePricingGroupSurcharge: g.day_wise_pricing_group_surcharge,
            }))
    }, [dayWisePricingScopeId, slices, selectedContract?.flag_day_wise_pricing, selectedContract?.day_wise_pricing])

    const handleDayWiseSurchargeAmountChange = useCallback(
        (groupId: string | null, amount: number | null, flagDifferent: boolean | null, isPercentage?: boolean) => {
            if (!dayWisePricingScopeId || !selectedContract?.contract_id || !onFullChange) return
            const slice = slices[dayWisePricingScopeId]
            const baseGroups =
                slice?.dayWiseGroups ?? dayWisePricingApiToLocal(selectedContract.day_wise_pricing)
            const updatedNextGroups = baseGroups.map((g) => {
                if (String(g.day_wise_pricing_group_id) === String(groupId)) {
                    if (flagDifferent != null) {
                        const dayWisePricingGroupSurcharge = Array.isArray(g.day_wise_pricing_group_surcharge)
                            ? g.day_wise_pricing_group_surcharge
                            : []
                        const existingSurcharge = dayWisePricingGroupSurcharge.find(
                            (s) => s.flag_different_surcharge === flagDifferent,
                        )
                        if (existingSurcharge) {
                            return {
                                ...g,
                                day_wise_pricing_group_surcharge: dayWisePricingGroupSurcharge.map((s) =>
                                    s.flag_different_surcharge === flagDifferent
                                        ? {
                                              ...s,
                                              surcharge_amount: amount,
                                              ...(isPercentage !== undefined
                                                  ? { flag_surcharge_amount_is_percentage: isPercentage }
                                                  : {}),
                                          }
                                        : s,
                                ),
                            }
                        }
                        return {
                            ...g,
                            day_wise_pricing_group_surcharge: [
                                ...dayWisePricingGroupSurcharge,
                                {
                                    flag_different_surcharge: flagDifferent,
                                    surcharge_amount: amount,
                                    flag_surcharge_amount_is_percentage: isPercentage === true,
                                },
                            ],
                        }
                    } else {
                        return {
                            ...g,
                            surcharge_amount: amount,
                            ...(isPercentage !== undefined
                                ? { flag_surcharge_amount_is_percentage: isPercentage }
                                : {}),
                        }
                    }
                }
                return g
            })
            setSlice(dayWisePricingScopeId, {
                flag_day_wise_pricing: slice?.flag_day_wise_pricing ?? !!selectedContract.flag_day_wise_pricing,
                dayWiseGroups: updatedNextGroups,
            })
            onFullChange(selectedContract.contract_id, { day_wise_pricing: localDayWiseGroupsToApi(updatedNextGroups) })
        },
        [dayWisePricingScopeId, onFullChange, selectedContract, setSlice, slices],
    )

    // Determine rate structure type and day group settings
    const rateStructureType = selectedContract?.rate_structure_type_id || RateStructureType.SIMPLE
    const showExtraBedBySeason = selectedContract?.flag_seasonal_extra_bed_pricing || false
    
    // Get day groups that should be shown in rate table
    const dayGroupsInRateTable = useMemo(() => {
        if (!selectedContract?.day_wise_pricing) return []
        return selectedContract.day_wise_pricing
            .filter((group: any) => group.flag_enable_day_group_in_rate_table === true)
            .sort((a: any, b: any) => {
                const aSeq = typeof a?.sequence === "number" && Number.isFinite(a.sequence) ? a.sequence : 0
                const bSeq = typeof b?.sequence === "number" && Number.isFinite(b.sequence) ? b.sequence : 0
                return (aSeq - bSeq) || String(a?.day_wise_pricing_group_id).localeCompare(String(b?.day_wise_pricing_group_id))
            })
    }, [selectedContract])

    // Map: roomTypeId -> enabled meal plan type IDs (from Room Categories)
    const roomTypeMealPlanMeta = useMemo(() => {
        const byRoomTypeId: Record<string, Set<number>> = {}
        const union = new Set<number>()

        hotelRoomTypes.forEach((rt) => {
            const roomTypeId = rt.hotel_room_type_id
            if (!roomTypeId) return

            const ids = (rt.meal_plan_type || [])
                .map((mp) => mp.meal_plan_type_id)
                .filter((id): id is number => typeof id === "number" && Number.isFinite(id))

            const set = new Set<number>(ids)
            byRoomTypeId[roomTypeId] = set
            set.forEach((id) => union.add(id))
        })

        return { byRoomTypeId, union }
    }, [hotelRoomTypes])

    // Get meal plan types for Meal Plan structure
    const mealPlanTypes = useMemo(() => {
        if (rateStructureType !== RateStructureType.MEAL_PLAN) return []
        const fromContract = selectedContract?.meal_plan_type || []

        const sortByMealPlanTypeId = (a: any, b: any) => {
            const aId = typeof a?.meal_plan_type_id === "number" && Number.isFinite(a.meal_plan_type_id) ? a.meal_plan_type_id : Number.MAX_SAFE_INTEGER
            const bId = typeof b?.meal_plan_type_id === "number" && Number.isFinite(b.meal_plan_type_id) ? b.meal_plan_type_id : Number.MAX_SAFE_INTEGER
            return (aId - bId) || String(a?.meal_plan_type_name ?? "").localeCompare(String(b?.meal_plan_type_name ?? ""))
        }

        // If Room Categories has meal plan selections, only show those meal plans in Rates
        if (roomTypeMealPlanMeta.union.size > 0) {
            return [...fromContract]
                .filter((mp) => roomTypeMealPlanMeta.union.has(mp.meal_plan_type_id))
                // Keep Rates meal plan sequence: BB -> HB -> FB -> AI (uses meal_plan_type_id order)
                .sort(sortByMealPlanTypeId)
        }

        // Fallback (e.g., room categories not loaded yet)
        return [...fromContract].sort(sortByMealPlanTypeId)
    }, [rateStructureType, selectedContract, roomTypeMealPlanMeta.union])

    const isMealPlanApplicableForRoomType = useCallback(
        (roomTypeId: string, mealPlanTypeId?: number) => {
            if (rateStructureType !== RateStructureType.MEAL_PLAN) return true
            if (!mealPlanTypeId) return true
            const set = roomTypeMealPlanMeta.byRoomTypeId[roomTypeId]
            return !!set && set.has(mealPlanTypeId)
        },
        [rateStructureType, roomTypeMealPlanMeta.byRoomTypeId]
    )

    // Derive day group prices from contract (updates when contract or its pricing array arrives)
    const dayGroupPricesFromContract = useMemo((): { [key: string]: DayGroupPricingData } => {
        // We always use HotelRoomDayGroupPricingModel for get/update
        const dayGroupPricingList: any[] = selectedContract?.hotel_room_day_group_pricing ?? []
        if (dayGroupPricingList.length === 0) return {}
        const map: { [key: string]: DayGroupPricingData } = {}
        dayGroupPricingList.forEach((pricing: any) => {
            const roomTypeId = pricing.hotel_room_type_id
            const dayGroupId = pricing.day_wise_pricing_group_id
            const key = buildDayGroupPriceKey(
                roomTypeId,
                dayGroupId,
                pricing.meal_plan_type_id,
                pricing.git_fit_type_id
            )
            const rawPrice = pricing.surcharge_amount
            const numPrice =
                rawPrice != null && rawPrice !== ""
                    ? (typeof rawPrice === "number" ? rawPrice : Number(rawPrice))
                    : null
            const price = numPrice != null && Number.isFinite(numPrice) ? numPrice : null
            const id = pricing.hotel_room_day_group_pricing_id ?? null
            map[key] = { id, price }
        })
        return map
    }, [selectedContract])

    // Sync contract data into state when it becomes available (fixes first-render-empty timing)
    useEffect(() => {
        const contractId = selectedContract?.contract_id ?? null
        const contractChanged = dayGroupPricesLastContractIdRef.current !== contractId

        if (contractChanged) {
            dayGroupPricesLastContractIdRef.current = contractId
            dayGroupPricesDirtyRef.current = false
        }

        // Avoid wiping unsaved user edits when parent re-renders contract object
        if (!dayGroupPricesDirtyRef.current || contractChanged) {
            setDayGroupPrices(dayGroupPricesFromContract)
        }
    }, [dayGroupPricesFromContract, selectedContract?.contract_id])

    // Initialize local prices from selected contract
    useEffect(() => {
        if (selectedContract?.hotel_room_pricing) {
            setLocalPrices(selectedContract.hotel_room_pricing)
        } else {
            setLocalPrices([])
        }
    }, [selectedContract])

    // Initialize meal plans from selected contract.
    // IMPORTANT: keep local "draft" rows (so + Meal Plan works) unless user switched contracts.
    useEffect(() => {
        const contractId = selectedContract?.contract_id ?? null
        const contractChanged = mealPlansLastContractIdRef.current !== contractId

        if (contractChanged) {
            mealPlansLastContractIdRef.current = contractId
            mealPlansDirtyRef.current = false
        }

        // If user hasn't edited meal plans for this contract (or contract just changed), sync from contract
        if (!mealPlansDirtyRef.current || contractChanged) {
            const fromContract = selectedContract?.meal_plan ?? []
            const mapped = fromContract.map((mp) => ({
                meal_plan_id: mp.meal_plan_id ?? null,
                meal_plan_name: mp.meal_plan_name ?? "",
                adult_charge: toPositiveNumberFromUnknownOrNull((mp as any).adult_charge),
                child_charge: toPositiveNumberFromUnknownOrNull((mp as any).child_charge),
            }))

            // If no data found, default show one row
            setMealPlans(mapped.length > 0 ? mapped : [emptyMealPlanRow()])
        }
    }, [selectedContract?.contract_id, selectedContract?.meal_plan])

    // Initialize extra bed price, child without bed price, surcharge per additional pax, and day-wise surcharges from contract
    useEffect(() => {
        if (selectedContract) {
            setExtraBedPrice(selectedContract.extra_bed_price || null)
            setChildWithoutBedPrice(selectedContract.child_without_bed_price ?? null)
            setChildWithBed(selectedContract.child_with_bed ?? null)
            setAdultExtraBedPrice(selectedContract.adult_with_bed ?? null)
            setSurchargePerAdditionalPax(selectedContract.surcharge_per_additional_pax ?? null)
            setRoomSpecificDifferentSurcharge(((selectedContract as any)?.room_specific_different_surcharge ?? []) as RoomSpecificDifferentSurchargeModel[])

            // Initialize seasonal extra bed rates (one entry per contract season; pricing rows may omit new seasons)
            if (selectedContract.flag_seasonal_extra_bed_pricing) {
                const fromPricing: { [seasonId: string]: number | null } = {}
                const fromPercentage: { [seasonId: string]: boolean } = {}
                ;(selectedContract.seasonal_extra_bed_pricing || []).forEach((pricing: any) => {
                    if (pricing.season_id) {
                        fromPricing[pricing.season_id] = pricing.charges ?? null
                        fromPercentage[pricing.season_id] = pricing.flag_charges_is_percentage === true
                    }
                })
                const merged: { [seasonId: string]: number | null } = {}
                const mergedPercentage: { [seasonId: string]: boolean } = {}
                ;(selectedContract.season || [])
                    .filter((s) => s.season_id && s.season_name)
                    .forEach((s) => {
                        merged[s.season_id!] = fromPricing[s.season_id!] ?? null
                        mergedPercentage[s.season_id!] = fromPercentage[s.season_id!] === true
                    })
                setExtraBedRates(merged)
                setExtraBedRateIsPercentage(mergedPercentage)
            } else {
                setExtraBedRates({})
                setExtraBedRateIsPercentage({})
            }

        }
    }, [selectedContract])

    const commitRoomSpecificDifferentSurcharge = useCallback(
        (next: RoomSpecificDifferentSurchargeModel[]) => {
            setRoomSpecificDifferentSurcharge(next)
            if (onFullChange && selectedContract?.contract_id) {
                onFullChange(selectedContract.contract_id, { room_specific_different_surcharge: next } as any)
            }
        },
        [onFullChange, selectedContract?.contract_id]
    )

    // Get room types from hotel and seasons from the contract
    const { roomTypes, seasons } = useMemo(() => {
        if (!selectedContract) return { roomTypes: [], seasons: [] }

        // Use hotel room types from the main hotel data
        const roomTypesFromHotel = hotelRoomTypes
            .filter((rt) => rt.hotel_room_type_id && rt.hotel_room_type_name)
            .map((rt) => ({
                id: rt.hotel_room_type_id!,
                name: rt.hotel_room_type_name!,
                bedTypeName: rt.bed_type_name!,
            }))

        // Get seasons from contract
        const seasonsFromContract = (selectedContract.season || [])
            .filter((s) => s.season_id && s.season_name)
            .map((s) => ({
                id: s.season_id!,
                name: s.season_name!,
            }))

        return {
            roomTypes: roomTypesFromHotel,
            seasons: seasonsFromContract,
        }
    }, [selectedContract, hotelRoomTypes])

    /** When seasonal extra bed pricing is on, contract must not carry a single Adult Extra Bed surcharge (UI uses per-season table). */
    useEffect(() => {
        if (!showExtraBedBySeason || !onFullChange || !selectedContract?.contract_id) return

        const patch: Partial<HotelContractModel> = {}
        if (selectedContract.adult_with_bed != null || selectedContract.flag_adult_with_bed_is_percentage) {
            patch.adult_with_bed = null
            patch.flag_adult_with_bed_is_percentage = false
        }
        const rows = (((selectedContract as any).room_specific_different_surcharge ?? []) as RoomSpecificDifferentSurchargeModel[]) || []
        let rowsChanged = false
        const nextRows = rows.map((r) => {
            if (r.adult_extra_bed_surcharge != null || r.flag_adult_extra_bed_surcharge_is_percentage) {
                rowsChanged = true
                return {
                    ...r,
                    adult_extra_bed_surcharge: null,
                    flag_adult_extra_bed_surcharge_is_percentage: false,
                }
            }
            return r
        })
        if (rowsChanged) {
            ;(patch as any).room_specific_different_surcharge = nextRows
        }
        if (Object.keys(patch).length === 0) return
        onFullChange(selectedContract.contract_id, patch as any)
    }, [showExtraBedBySeason, onFullChange, selectedContract])

    const handleExtraBedRatesChange = useCallback(
        (next: Record<string, number | null>) => {
            setExtraBedRates(next)
            if (!onFullChange || !selectedContract?.contract_id || !showExtraBedBySeason) return
            const prevPricing = (selectedContract.seasonal_extra_bed_pricing || []) as SeasonalExtraBedPricingModel[]
            const idBySeason: Record<string, string | null | undefined> = {}
            prevPricing.forEach((p) => {
                if (p.season_id) idBySeason[p.season_id] = p.seasonal_extra_bed_price_id ?? null
            })
            const seasonal_extra_bed_pricing: SeasonalExtraBedPricingModel[] = seasons.map((s) => ({
                seasonal_extra_bed_price_id: idBySeason[s.id] ?? null,
                season_id: s.id,
                season_name: s.name,
                charges: next[s.id] ?? null,
                flag_charges_is_percentage: extraBedRateIsPercentage[s.id] === true,
            }))
            onFullChange(selectedContract.contract_id, { seasonal_extra_bed_pricing })
        },
        [onFullChange, selectedContract, seasons, showExtraBedBySeason, extraBedRateIsPercentage],
    )

    const handleExtraBedRateIsPercentageChange = useCallback(
        (seasonId: string, isPercentage: boolean) => {
            const nextRates = { ...extraBedRates }
            const nextFlags = { ...extraBedRateIsPercentage, [seasonId]: isPercentage }
            setExtraBedRateIsPercentage(nextFlags)
            if (!onFullChange || !selectedContract?.contract_id || !showExtraBedBySeason) return
            const prevPricing = (selectedContract.seasonal_extra_bed_pricing || []) as SeasonalExtraBedPricingModel[]
            const idBySeason: Record<string, string | null | undefined> = {}
            prevPricing.forEach((p) => {
                if (p.season_id) idBySeason[p.season_id] = p.seasonal_extra_bed_price_id ?? null
            })
            const seasonal_extra_bed_pricing: SeasonalExtraBedPricingModel[] = seasons.map((s) => ({
                seasonal_extra_bed_price_id: idBySeason[s.id] ?? null,
                season_id: s.id,
                season_name: s.name,
                charges: nextRates[s.id] ?? null,
                flag_charges_is_percentage: nextFlags[s.id] === true,
            }))
            onFullChange(selectedContract.contract_id, { seasonal_extra_bed_pricing })
        },
        [extraBedRates, extraBedRateIsPercentage, onFullChange, selectedContract, seasons, showExtraBedBySeason],
    )

    

    // Build dynamic columns based on rate structure and day group settings
    const tableColumns = useMemo((): TableColumn[] => {
        const columns: TableColumn[] = []

        // Scenario 1: Simple + No Day Groups (current behavior)
        if (rateStructureType === RateStructureType.SIMPLE && dayGroupsInRateTable.length === 0) {
            seasons.forEach((season) => {
                columns.push({
                    id: `season-${season.id}`,
                    label: season.name,
                    type: "season",
                    seasonId: season.id,
                })
            })
        }
    
        // Scenario 2: Simple + Day Groups Enabled
        else if (rateStructureType === RateStructureType.SIMPLE && dayGroupsInRateTable.length > 0) {
            // Add season columns first
            seasons.forEach((season) => {
                columns.push({
                    id: `season-${season.id}`,
                    label: season.name,
                    type: "season",
                    seasonId: season.id,
                })
            })
            // Add day group columns
            dayGroupsInRateTable.forEach((group: any) => {
                columns.push({
                    id: `daygroup-${group.day_wise_pricing_group_id}`,
                    label: group.day_wise_pricing_group_name || "Day Group",
                    type: "day_group",
                    dayGroupId: group.day_wise_pricing_group_id,
                })
            })
        }

        // Scenario 3: Meal Plan + No Day Groups
        else if (rateStructureType === RateStructureType.MEAL_PLAN && dayGroupsInRateTable.length === 0) {
            seasons.forEach((season) => {
                mealPlanTypes.forEach((mealPlan) => {
                    columns.push({
                        id: `season-${season.id}-meal-${mealPlan.meal_plan_type_id}`,
                        label: `${season.name} - ${mealPlan.meal_plan_type_name}`,
                        type: "season",
                        seasonId: season.id,
                        mealPlanTypeId: mealPlan.meal_plan_type_id,
                    })
                })
            })
        }

        // Scenario 4: Meal Plan + Day Groups Enabled
        else if (rateStructureType === RateStructureType.MEAL_PLAN && dayGroupsInRateTable.length > 0) {
            // Add season × meal plan columns first
            seasons.forEach((season) => {
                mealPlanTypes.forEach((mealPlan) => {
                    columns.push({
                        id: `season-${season.id}-meal-${mealPlan.meal_plan_type_id}`,
                        label: `${season.name} - ${mealPlan.meal_plan_type_name}`,
                        type: "season",
                        seasonId: season.id,
                        mealPlanTypeId: mealPlan.meal_plan_type_id,
                    })
                })
            })
            // Add day group × meal plan columns
            dayGroupsInRateTable.forEach((group: any) => {
                mealPlanTypes.forEach((mealPlan) => {
                    columns.push({
                        id: `daygroup-${group.day_wise_pricing_group_id}-meal-${mealPlan.meal_plan_type_id}`,
                        label: `${group.day_wise_pricing_group_name} - ${mealPlan.meal_plan_type_name}`,
                        type: "day_group",
                        dayGroupId: group.day_wise_pricing_group_id,
                        mealPlanTypeId: mealPlan.meal_plan_type_id,
                    })
                })
            })
        }

        // Scenario 5: GIT/FIT + No Day Groups
        else if (rateStructureType === RateStructureType.GIT_FIT && dayGroupsInRateTable.length === 0) {
            seasons.forEach((season) => {
                columns.push({
                    id: `season-${season.id}-git`,
                    label: `${season.name} - GIT Rate`,
                    type: "season",
                    seasonId: season.id,
                    gitFitTypeId: GitFitType.GIT,
                })
                columns.push({
                    id: `season-${season.id}-fit`,
                    label: `${season.name} - FIT Rate`,
                    type: "season",
                    seasonId: season.id,
                    gitFitTypeId: GitFitType.FIT,
                })
                
            })
        }

        // Scenario 6: GIT/FIT + Day Groups Enabled
        else if (rateStructureType === RateStructureType.GIT_FIT && dayGroupsInRateTable.length > 0) {
            // Add season × GIT/FIT columns first
            seasons.forEach((season) => {
                columns.push({
                    id: `season-${season.id}-git`,
                    label: `${season.name} - GIT Rate`,
                    type: "season",
                    seasonId: season.id,
                    gitFitTypeId: GitFitType.GIT,
                })
                columns.push({
                    id: `season-${season.id}-fit`,
                    label: `${season.name} - FIT Rate`,
                    type: "season",
                    seasonId: season.id,
                    gitFitTypeId: GitFitType.FIT,
                })
                
            })
            // Add day group × GIT/FIT columns
            dayGroupsInRateTable.forEach((group: any) => {
                columns.push({
                    id: `daygroup-${group.day_wise_pricing_group_id}-git`,
                    label: `${group.day_wise_pricing_group_name} - GIT Rate`,
                    type: "day_group",
                    dayGroupId: group.day_wise_pricing_group_id,
                    gitFitTypeId: GitFitType.GIT,
                })
                columns.push({
                    id: `daygroup-${group.day_wise_pricing_group_id}-fit`,
                    label: `${group.day_wise_pricing_group_name} - FIT Rate`,
                    type: "day_group",
                    dayGroupId: group.day_wise_pricing_group_id,
                    gitFitTypeId: GitFitType.FIT,
                })
            })
        }

        return columns
    }, [rateStructureType, seasons, dayGroupsInRateTable, mealPlanTypes])

    const hasDayGroupsInRateTable = dayGroupsInRateTable.length > 0

    const pricingScopes = useMemo((): PricingScope[] => {
        const seasonColumns = tableColumns.filter((c) => c.type === "season")
        const dayGroupColumns = tableColumns.filter((c) => c.type === "day_group")

        // Scope ids come from all columns — a contract without seasons only has day group columns
        if (rateStructureType === RateStructureType.MEAL_PLAN) {
            const mealPlanIds = [
                ...new Set(
                    tableColumns
                        .map((c) => c.mealPlanTypeId)
                        .filter((id): id is number => typeof id === "number" && Number.isFinite(id)),
                ),
            ]
            return mealPlanIds.map((mealPlanTypeId) => ({
                mealPlanTypeId,
                seasonColumns: seasonColumns.filter((c) => c.mealPlanTypeId === mealPlanTypeId),
                dayGroupColumns: dayGroupColumns.filter((c) => c.mealPlanTypeId === mealPlanTypeId),
            }))
        }

        if (rateStructureType === RateStructureType.GIT_FIT) {
            const gitFitIds = [
                ...new Set(
                    tableColumns
                        .map((c) => c.gitFitTypeId)
                        .filter((id): id is number => typeof id === "number" && Number.isFinite(id)),
                ),
            ]
            return gitFitIds.map((gitFitTypeId) => ({
                gitFitTypeId,
                seasonColumns: seasonColumns.filter((c) => c.gitFitTypeId === gitFitTypeId),
                dayGroupColumns: dayGroupColumns.filter((c) => c.gitFitTypeId === gitFitTypeId),
            }))
        }

        return [{ seasonColumns, dayGroupColumns }]
    }, [tableColumns, rateStructureType])

    const getSeasonPriceForColumn = useCallback(
        (roomTypeId: string, column: TableColumn): number | null => {
            const priceEntry = localPrices.find(
                (p) =>
                    p.hotel_room_type_id === roomTypeId &&
                    p.season_id === column.seasonId &&
                    (column.mealPlanTypeId ? p.meal_plan_type_id === column.mealPlanTypeId : !p.meal_plan_type_id || p.meal_plan_type_id === null) &&
                    (column.gitFitTypeId ? p.git_fit_type_id === column.gitFitTypeId : !p.git_fit_type_id || p.git_fit_type_id === null),
            )
            return priceEntry?.price ?? null
        },
        [localPrices],
    )

    const getDayGroupPriceForColumn = useCallback(
        (roomTypeId: string, column: TableColumn): number | null => {
            const key = buildDayGroupPriceKey(roomTypeId, column.dayGroupId, column.mealPlanTypeId, column.gitFitTypeId)
            return dayGroupPrices[key]?.price ?? null
        },
        [dayGroupPrices],
    )

    // Transform flat pricing data into
    //  table structure
    const tableData = useMemo((): RoomTypeRow[] => {
        const rows: RoomTypeRow[] = roomTypes.map((roomType) => {
            const prices: { [columnId: string]: PriceData } = {}

            tableColumns.forEach((column) => {
                if (column.type === "season") {
                    // Find price for this season (and meal plan/git-fit if applicable)
                    const priceEntry = localPrices.find(
                        (p) =>
                            p.hotel_room_type_id === roomType.id &&
                            p.season_id === column.seasonId &&
                            (column.mealPlanTypeId ? p.meal_plan_type_id === column.mealPlanTypeId : !p.meal_plan_type_id || p.meal_plan_type_id === null) &&
                            (column.gitFitTypeId ? p.git_fit_type_id === column.gitFitTypeId : !p.git_fit_type_id || p.git_fit_type_id === null)
                    )
                    prices[column.id] = {
                        priceId: priceEntry?.hotel_room_pricing_id || null,
                        price: priceEntry?.price ?? null,
                        roomTypeId: roomType.id,
                        seasonId: column.seasonId,
                        mealPlanTypeId: column.mealPlanTypeId,
                        gitFitTypeId: column.gitFitTypeId,
                    }
                } else if (column.type === "day_group") {
                    // Find price for this day group (same key format as init)
                    const key = buildDayGroupPriceKey(
                        roomType.id,
                        column.dayGroupId,
                        column.mealPlanTypeId,
                        column.gitFitTypeId
                    )
                    const dayGroupData = dayGroupPrices[key]
                    prices[column.id] = {
                        priceId: dayGroupData?.id ?? null,
                        price: dayGroupData?.price ?? null,
                        roomTypeId: roomType.id,
                        dayGroupId: column.dayGroupId,
                        mealPlanTypeId: column.mealPlanTypeId,
                        gitFitTypeId: column.gitFitTypeId,
                    }
                }
            })

            return {
                roomTypeId: roomType.id,
                roomTypeName: roomType.name,
                bedTypeName: roomType.bedTypeName,
                prices,
            }
        })
        return rows
    }, [roomTypes, tableColumns, localPrices, dayGroupPrices])

    // Update price for a specific cell
    const updatePrice = (priceData: PriceData, newPrice: number | null) => {
        if (priceData.seasonId) {
            // Update season-based pricing
            const roomType = roomTypes.find((rt) => rt.id === priceData.roomTypeId)
            const season = seasons.find((s) => s.id === priceData.seasonId)

            if (!roomType || !season) return

            const existingPriceIndex = localPrices.findIndex(
                (p) =>
                    p.hotel_room_type_id === priceData.roomTypeId &&
                    p.season_id === priceData.seasonId &&
                    (priceData.mealPlanTypeId ? p.meal_plan_type_id === priceData.mealPlanTypeId : !p.meal_plan_type_id || p.meal_plan_type_id === null) &&
                    (priceData.gitFitTypeId ? p.git_fit_type_id === priceData.gitFitTypeId : !p.git_fit_type_id || p.git_fit_type_id === null)
            )

            let updatedPrices: HotelRoomPricingModel[]

            if (existingPriceIndex >= 0) {
                // Update existing price
                updatedPrices = [...localPrices]
                updatedPrices[existingPriceIndex] = {
                    ...updatedPrices[existingPriceIndex],
                    price: newPrice,
                }
            } else {
                // Create new price entry
                const newPriceEntry: HotelRoomPricingModel = {
                    hotel_room_pricing_id: null,
                    hotel_room_type_id: priceData.roomTypeId,
                    hotel_room_type_name: roomType.name,
                    season_id: priceData.seasonId,
                    season_name: season.name,
                    price: newPrice,
                    surcharge_per_additional_pax: null,
                    meal_plan_type_id: priceData.mealPlanTypeId || null,
                    git_fit_type_id: priceData.gitFitTypeId || null,
                }
                updatedPrices = [...localPrices, newPriceEntry]
            }

            setLocalPrices(updatedPrices)
            if (onChange) {
                onChange(updatedPrices)
            }
        } else if (priceData.dayGroupId) {
            // Update day group pricing - preserve the ID
            const key = buildDayGroupPriceKey(
                priceData.roomTypeId,
                priceData.dayGroupId,
                priceData.mealPlanTypeId,
                priceData.gitFitTypeId
            )
            dayGroupPricesDirtyRef.current = true
            setDayGroupPrices((prev) => ({
                ...prev,
                [key]: {
                    id: prev[key]?.id || null,
                    price: newPrice,
                },
            }))
        }
    }

    const hasPriceError = (price: number | null) => {
        return (showValidationErrors || localValidationErrors) && (price === null || price === undefined)
    }

    const isMealPlanRowTouched = useCallback((row: MealPlanRowLocal) => {
        return !!toTrimmedOrNull(row.meal_plan_name) || row.adult_charge !== null || row.child_charge !== null
    }, [])

    const getNormalizedMealPlans = useCallback((rows: MealPlanRowLocal[]): MealPlanModel[] => {
        const activeRows = rows.filter((r) => isMealPlanRowTouched(r))
        return activeRows.map((mp) => ({
            meal_plan_id: mp.meal_plan_id ?? null,
            meal_plan_name: mp.meal_plan_name.trim(),
            adult_charge: mp.adult_charge ?? 0,
            child_charge: mp.child_charge ?? 0,
        }))
    }, [isMealPlanRowTouched])

    // Transform contracts into select options
    const contractOptions = useMemo(() => {
        return contracts.map((contract, index) => ({
            value: contract.contract_id || `contract-${index}`,
            label: `${moment(contract.contract_start_date).format("DD-MMM-YYYY")} to ${moment(contract.contract_end_date).format("DD-MMM-YYYY")}`,
        }))
    }, [contracts])

    const isScopeColumnApplicable = useCallback(
        (roomTypeId: string, column: TableColumn): boolean => {
            if (rateStructureType === RateStructureType.MEAL_PLAN && column.mealPlanTypeId) {
                return isMealPlanApplicableForRoomType(roomTypeId, column.mealPlanTypeId)
            }
            return true
        },
        [rateStructureType, isMealPlanApplicableForRoomType],
    )

    const scopeRequiresDayGroupPrices = useCallback(
        (roomTypeId: string, scope: PricingScope): boolean => {
            return scope.seasonColumns.some((column) => {
                if (!isScopeColumnApplicable(roomTypeId, column)) return false
                const seasonPrice = getSeasonPriceForColumn(roomTypeId, column)
                return seasonPrice === 0
            })
        },
        [getSeasonPriceForColumn, isScopeColumnApplicable],
    )

    const validateSeasonZeroPricing = useCallback((): boolean => {
        for (const roomType of roomTypes) {
            for (const scope of pricingScopes) {
                // Without seasons a day group price cannot be zero (same rule as a season-only table)
                if (scope.seasonColumns.length === 0) {
                    const hasZeroDayGroupPrice = scope.dayGroupColumns.some(
                        (dayColumn) =>
                            isScopeColumnApplicable(roomType.id, dayColumn) &&
                            getDayGroupPriceForColumn(roomType.id, dayColumn) === 0,
                    )
                    if (hasZeroDayGroupPrice) return false
                    continue
                }

                for (const column of scope.seasonColumns) {
                    if (!isScopeColumnApplicable(roomType.id, column)) continue

                    const seasonPrice = getSeasonPriceForColumn(roomType.id, column)
                    if (seasonPrice === null || seasonPrice === undefined) continue

                    if (!hasDayGroupsInRateTable) {
                        if (seasonPrice === 0) return false
                        continue
                    }

                    if (seasonPrice > 0) continue

                    const applicableDayGroupColumns = scope.dayGroupColumns.filter((dayColumn) =>
                        isScopeColumnApplicable(roomType.id, dayColumn),
                    )
                    if (applicableDayGroupColumns.length === 0) return false

                    const allDayGroupsPositive = applicableDayGroupColumns.every((dayColumn) => {
                        const dayPrice = getDayGroupPriceForColumn(roomType.id, dayColumn)
                        return dayPrice !== null && dayPrice !== undefined && dayPrice > 0
                    })
                    if (!allDayGroupsPositive) return false
                }
            }
        }

        return true
    }, [
        roomTypes,
        pricingScopes,
        isScopeColumnApplicable,
        getSeasonPriceForColumn,
        getDayGroupPriceForColumn,
        hasDayGroupsInRateTable,
    ])

    // Validate required (non-null) pricing fields
    const validateRequiredFields = useCallback((): boolean => {
        let isValid = true

        // Validate all pricing cells based on table columns
        for (const roomType of roomTypes) {
            for (const scope of pricingScopes) {
                // Without seasons the day group prices are the only prices, so they are always required
                const requiresDayGroupPrices =
                    hasDayGroupsInRateTable &&
                    (scope.seasonColumns.length === 0 || scopeRequiresDayGroupPrices(roomType.id, scope))

                for (const column of scope.seasonColumns) {
                    if (!isScopeColumnApplicable(roomType.id, column)) continue

                    const seasonPrice = getSeasonPriceForColumn(roomType.id, column)
                    if (seasonPrice === null || seasonPrice === undefined) {
                        isValid = false
                        break
                    }
                }
                if (!isValid) break

                if (requiresDayGroupPrices) {
                    for (const column of scope.dayGroupColumns) {
                        if (!isScopeColumnApplicable(roomType.id, column)) continue

                        const dayPrice = getDayGroupPriceForColumn(roomType.id, column)
                        if (dayPrice === null || dayPrice === undefined) {
                            isValid = false
                            break
                        }
                    }
                }
            }
            if (!isValid) break
        }

        // Validate meal plans only when row is "touched" (table is optional)
        for (const mp of mealPlans) {
            if (!isMealPlanRowTouched(mp)) continue
            const hasValidName = !!toTrimmedOrNull(mp.meal_plan_name)
            const hasValidAdult = mp.adult_charge !== null && mp.adult_charge !== undefined
            const hasValidChild = mp.child_charge !== null && mp.child_charge !== undefined
            if (!hasValidName || !hasValidAdult || !hasValidChild) {
                isValid = false
                break
            }
        }

        return isValid
    }, [
        roomTypes,
        pricingScopes,
        hasDayGroupsInRateTable,
        scopeRequiresDayGroupPrices,
        isScopeColumnApplicable,
        getSeasonPriceForColumn,
        getDayGroupPriceForColumn,
        mealPlans,
        isMealPlanRowTouched,
    ])

    const validateAllFields = useCallback((): boolean => {
        return validateRequiredFields() && validateSeasonZeroPricing()
    }, [validateRequiredFields, validateSeasonZeroPricing])

    // Save hotel room pricing for the selected contract
    const handleSave = useCallback(async (): Promise<boolean> => {
        if (!selectedContract?.contract_id) {
            showError("No contract selected")
            return false
        }

        if (!validateRequiredFields()) {
            setLocalValidationErrors(true)
            showError("Please fill in all required fields")
            return false
        }

        if (!validateSeasonZeroPricing()) {
            setLocalValidationErrors(true)
            if (seasons.length === 0) {
                showError("Day group price cannot be zero.")
            } else if (hasDayGroupsInRateTable) {
                showError(
                    "When a season price is zero, all day-wise group prices for that room must be greater than zero.",
                )
            } else {
                showError("Season price cannot be zero.")
            }
            return false
        }

        setIsSaving(true)
        setLocalValidationErrors(false)

        try {
            // Prepare seasonal_extra_bed_price for PUT /api/hotel/hotel-room-pricing (same shape as contract seasonal_extra_bed_pricing, keyed for API)
            const prevSeasonalExtraBed = (selectedContract.seasonal_extra_bed_pricing || []) as SeasonalExtraBedPricingModel[]
            const seasonalPriceIdBySeason: Record<string, string | null | undefined> = {}
            prevSeasonalExtraBed.forEach((p) => {
                if (p.season_id) seasonalPriceIdBySeason[p.season_id] = p.seasonal_extra_bed_price_id ?? null
            })
            const seasonalExtraBedPrice = showExtraBedBySeason
                ? seasons.map((season) => {
                      const raw = extraBedRates[season.id]
                      const charges = raw != null && Number.isFinite(Number(raw)) ? Number(raw) : 0
                      return {
                          seasonal_extra_bed_price_id: seasonalPriceIdBySeason[season.id] ?? null,
                          season_id: season.id,
                          season_name: season.name,
                          charges,
                          flag_charges_is_percentage: extraBedRateIsPercentage[season.id] === true,
                      }
                  })
                : []

            // Prepare hotel_room_pricing array with meal_plan_type_id and git_fit_type_id
            const hotelRoomPricing = localPrices.map((price) => ({
                hotel_room_pricing_id: price.hotel_room_pricing_id || null,
                hotel_room_type_id: price.hotel_room_type_id || "",
                hotel_room_type_name: price.hotel_room_type_name || "",
                season_id: price.season_id || "",
                meal_plan_type_id: price.meal_plan_type_id || null,
                git_fit_type_id: price.git_fit_type_id || null,
                price: price.price || 0,
            }))

            // Prepare hotel_room_day_group_pricing array
            const hotelRoomDayGroupPricing: any[] = []
            roomTypes.forEach((roomType) => {
                dayGroupsInRateTable.forEach((group: any) => {
                    if (rateStructureType === RateStructureType.SIMPLE) {
                        // Simple rate structure
                        const key = buildDayGroupPriceKey(roomType.id, group.day_wise_pricing_group_id, null, null)
                        const priceData = dayGroupPrices[key]
                        if (priceData && priceData.price !== null && priceData.price !== undefined) {
                            hotelRoomDayGroupPricing.push({
                                hotel_room_day_group_pricing_id: priceData.id,
                                hotel_room_type_id: roomType.id,
                                day_wise_pricing_group_id: group.day_wise_pricing_group_id,
                                meal_plan_type_id: null,
                                git_fit_type_id: null,
                                surcharge_amount: priceData.price,
                            })
                        }
                    } else if (rateStructureType === RateStructureType.MEAL_PLAN) {
                        // Meal Plan rate structure
                        mealPlanTypes.forEach((mealPlan) => {
                            const key = buildDayGroupPriceKey(
                                roomType.id,
                                group.day_wise_pricing_group_id,
                                mealPlan.meal_plan_type_id,
                                null
                            )
                            const priceData = dayGroupPrices[key]
                            if (priceData && priceData.price !== null && priceData.price !== undefined) {
                                hotelRoomDayGroupPricing.push({
                                    hotel_room_day_group_pricing_id: priceData.id,
                                    hotel_room_type_id: roomType.id,
                                    day_wise_pricing_group_id: group.day_wise_pricing_group_id,
                                    meal_plan_type_id: mealPlan.meal_plan_type_id,
                                    git_fit_type_id: null,
                                    surcharge_amount: priceData.price,
                                })
                            }
                        })
                    } else if (rateStructureType === RateStructureType.GIT_FIT) {
                        // GIT/FIT rate structure
                        const fitKey = buildDayGroupPriceKey(
                            roomType.id,
                            group.day_wise_pricing_group_id,
                            null,
                            GitFitType.FIT
                        )
                        const gitKey = buildDayGroupPriceKey(
                            roomType.id,
                            group.day_wise_pricing_group_id,
                            null,
                            GitFitType.GIT
                        )

                        const fitPriceData = dayGroupPrices[fitKey]
                        if (fitPriceData && fitPriceData.price !== null && fitPriceData.price !== undefined) {
                            hotelRoomDayGroupPricing.push({
                                hotel_room_day_group_pricing_id: fitPriceData.id,
                                hotel_room_type_id: roomType.id,
                                day_wise_pricing_group_id: group.day_wise_pricing_group_id,
                                meal_plan_type_id: null,
                                git_fit_type_id: GitFitType.FIT,
                                surcharge_amount: fitPriceData.price,
                            })
                        }
                        
                        const gitPriceData = dayGroupPrices[gitKey]
                        if (gitPriceData && gitPriceData.price !== null && gitPriceData.price !== undefined) {
                            hotelRoomDayGroupPricing.push({
                                hotel_room_day_group_pricing_id: gitPriceData.id,
                                hotel_room_type_id: roomType.id,
                                day_wise_pricing_group_id: group.day_wise_pricing_group_id,
                                meal_plan_type_id: null,
                                git_fit_type_id: GitFitType.GIT,
                                surcharge_amount: gitPriceData.price,
                            })
                        }
                    }
                })
            })

            const dayWiseGroupsForSave: DayWisePricingGroupLocal[] =
                dayWisePricingScopeId != null &&
                dayWisePricingScopeId !== "" &&
                slices[dayWisePricingScopeId]?.dayWiseGroups
                    ? slices[dayWisePricingScopeId]!.dayWiseGroups
                    : dayWisePricingApiToLocal(selectedContract.day_wise_pricing)
            const filteredDayWiseGroupForSave = dayWiseGroupsForSave.filter((value) => {
                if(typeof value.day_wise_pricing_group_id == "string" && value.day_wise_pricing_group_id?.toString().includes("default")) return false;
                return value;
            })

            const roomSpecificSurchargeForPayload = showExtraBedBySeason
                ? roomSpecificDifferentSurcharge.map((r) => ({
                      ...r,
                      adult_extra_bed_surcharge: null,
                      flag_adult_extra_bed_surcharge_is_percentage: false,
                  }))
                : roomSpecificDifferentSurcharge

            const payload: any = {
                hotel_id: hotelId,
                contract_id: selectedContract.contract_id,
                extra_bed_price: extraBedPrice,
                child_without_bed_price: childWithoutBedPrice,
                surcharge_per_additional_pax: surchargePerAdditionalPax,
                child_with_bed: childWithBed,
                adult_with_bed: showExtraBedBySeason ? null : adultExtraBedPrice,
                flag_adult_with_bed_is_percentage: showExtraBedBySeason
                    ? false
                    : selectedContract.flag_adult_with_bed_is_percentage === true,
                flag_child_with_bed_is_percentage: selectedContract.flag_child_with_bed_is_percentage === true,
                flag_child_without_bed_is_percentage: selectedContract.flag_child_without_bed_is_percentage === true,
                room_specific_different_surcharge: roomSpecificSurchargeForPayload,
                hotel_room_pricing: hotelRoomPricing,
                seasonal_extra_bed_price: seasonalExtraBedPrice,
                hotel_room_day_group_pricing: hotelRoomDayGroupPricing,
                day_wise_pricing: localDayWiseGroupsToRoomPricingDayWisePatch(filteredDayWiseGroupForSave),
            }

            // Add meal_plan (supports add/remove/clear per contract)
            const normalizedMealPlans = getNormalizedMealPlans(mealPlans)
            if (selectedContract.meal_plan !== undefined || normalizedMealPlans.length > 0) {
                payload.meal_plan = normalizedMealPlans
            }

            // Add per_person_seasonal_surcharge if it exists
            if (selectedContract.per_person_seasonal_surcharge) {
                payload.per_person_seasonal_surcharge = selectedContract.per_person_seasonal_surcharge
            }

            // Add peak_date_surcharge_room_type_group if it exists
            if (selectedContract.peak_date_surcharge_group) {
                payload.peak_date_surcharge_room_type_group = selectedContract.peak_date_surcharge_group.flatMap(
                    (group) => group.peak_date_surcharge_room_type_group || []
                )
            }

            // Add peak_date_surchage (contract-level amounts) if it exists
            const peakDateSurchage = (selectedContract as any)?.peak_date_surchage
            if (peakDateSurchage) {
                payload.peak_date_surchage = peakDateSurchage
            }

            const response = await agent.UpdateHotelRoomPricingAPI.put(payload)

            if (response?.status === false) {
                showError(response?.error?.message || "Failed to save hotel room pricing")
                return false
            }

            // After successful save, allow syncing from refreshed contract data
            mealPlansDirtyRef.current = false
            dayGroupPricesDirtyRef.current = false

            // Reset validation errors after successful save
            if (onValidationError) {
                onValidationError(false)
            }

            // Notify parent of successful save
            if (onSaveSuccess) {
                onSaveSuccess()
            }

            showSuccess("Hotel room pricing saved successfully!")
            return true
        } catch (error: any) {
            console.error("Error saving hotel room pricing:", error)
            showError(`Error saving hotel room pricing: ${error.message}`)
            return false
        } finally {
            setIsSaving(false)
        }
    }, [
        selectedContract,
        validateRequiredFields,
        validateSeasonZeroPricing,
        hasDayGroupsInRateTable,
        showError,
        showSuccess,
        showExtraBedBySeason,
        seasons,
        extraBedRates,
        extraBedRateIsPercentage,
        localPrices,
        roomTypes,
        dayGroupsInRateTable,
        rateStructureType,
        dayGroupPrices,
        mealPlanTypes,
        mealPlans,
        dayWisePricingScopeId,
        slices,
        hotelId,
        extraBedPrice,
        childWithoutBedPrice,
        surchargePerAdditionalPax,
        childWithBed,
        adultExtraBedPrice,
        roomSpecificDifferentSurcharge,
        getNormalizedMealPlans,
        onValidationError,
        onSaveSuccess,
    ])

    // Check for validation errors and notify parent
    useEffect(() => {
        if (!onValidationError) return

        const hasErrors = !validateAllFields()
        onValidationError(hasErrors)
    }, [localPrices, dayGroupPrices, roomTypes, seasons, onValidationError, validateAllFields])

    // Expose save function to parent
    useEffect(() => {
        if (onSaveRef) {
            onSaveRef(handleSave)
        }
    }, [onSaveRef, handleSave])

    if (!selectedContract) {
        return (
            <div className={styles.emptyState}>
                <p>No contracts available. Please create a contract first.</p>
            </div>
        )
    }

    if (roomTypes.length === 0 || hotelRoomTypes.length === 0) {
        return (
            <div className={styles.emptyState}>
                <p>No room types found. Please add and save room types in the Room Categories section first.</p>
            </div>
        )
    }

    // A contract may skip seasons when its day groups are shown in the rate table
    if (seasons.length === 0 && dayGroupsInRateTable.length === 0) {
        return (
            <div className={styles.emptyState}>
                <p>No seasons configured in this contract. Please configure seasons in the Rates tab of the contract.</p>
            </div>
        )
    }

    const perPersonSeasonalSurchargeFromContract =
        ((selectedContract as any)?.per_person_seasonal_surcharge ??
            (selectedContract as any)?.per_person_seasonal_surcharges ??
            (selectedContract as any)?.perPersonSeasonalSurcharge ??
            []) as any[]

    return (
        <>
            <div className={styles.ratesContainer}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                        {/* Contract Selector and Save Button */}
                        {contracts.length > 0 && (
                            <div className={styles.contractSelectorRow}>
                                <div className={styles.contractSelector}>
                                    <label style={{ fontWeight: 600, fontSize: 14, color: "black", paddingRight: "20px" }}>Contract</label>
                                    {contracts.length > 1 ? (
                                        <Select label="" value={selectedContract.contract_id || ""} onChange={(e) => onContractChange?.(e.target.value)} options={contractOptions} disabled={readOnly} width="300px" />
                                    ) : (
                                        <div className={styles.contractInfo}>
                                            {moment(selectedContract.contract_start_date).format("DD-MMM-YYYY")} to {moment(selectedContract.contract_end_date).format("DD-MMM-YYYY")}
                                        </div>
                                    )}
                                </div>
                                {!readOnly && (
                                    <button className={styles.saveButton} onClick={handleSave} disabled={isSaving}>
                                        {isSaving ? "Saving..." : "Save"}
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Rates Table */}
                        <div className={styles.tableContainer}>
                            <table className={styles.ratesTable}>
                                <thead>
                                    <tr>
                                        <th className={styles.roomTypeHeader}>Room Type</th>
                                        <th className={styles.beddingTypeHeader}>Bedding Type</th>
                                        {tableColumns.map((column) => (
                                            <th key={column.id} className={styles.seasonHeader} title={toTitleCase(column.label)}>
                                                {toTitleCase(column.label)} <span className={styles.required}>*</span>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {tableData.map((row) => (
                                        <tr key={row.roomTypeId}>
                                            <td className={styles.roomTypeCell} title={toTitleCase(row.roomTypeName)}>
                                                {toTitleCase(row.roomTypeName)}
                                            </td>
                                            <td className={styles.beddingTypeCell} title={toTitleCase(row.bedTypeName)}>
                                                {toTitleCase(row.bedTypeName)}
                                            </td>
                                            {tableColumns.map((column) => {
                                                const isApplicable =
                                                    rateStructureType !== RateStructureType.MEAL_PLAN ||
                                                    !column.mealPlanTypeId ||
                                                    isMealPlanApplicableForRoomType(row.roomTypeId, column.mealPlanTypeId)

                                                const priceData = row.prices[column.id]
                                                return (
                                                    <td key={column.id} className={styles.priceCell}>
                                                        {isApplicable ? (
                                                            <InputText
                                                                label={`${row.roomTypeName} - ${column.label}`}
                                                                hideLabel={true}
                                                                noMargin={true}
                                                                value={priceData.price === null ? "" : String(priceData.price)}
                                                                onChange={(e) => {
                                                                    const newPrice = toPositiveNumberOrNull(e.target.value)
                                                                    updatePrice(priceData, newPrice)
                                                                }}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === "-" || e.key === "e" || e.key === "E") {
                                                                        e.preventDefault()
                                                                    }
                                                                }}
                                                                type="number"
                                                                disabled={readOnly}
                                                                error={hasPriceError(priceData.price) ? "Required" : undefined}
                                                            />
                                                        ) : (
                                                            <span className={styles.naCell}>N/A</span>
                                                        )}
                                                    </td>
                                                )
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                        <ThirdPersonSurchargeConfiguration
                            rateStructureTypeId={rateStructureType}
                            mealPlanTypes={mealPlanTypes}
                            hotelRoomTypes={hotelRoomTypes}
                            dayWiseSurchargeRows={dayWiseSurchargeRows}
                            onDayWiseSurchargeAmountChange={handleDayWiseSurchargeAmountChange}
                            surchargePerAdditionalPax={surchargePerAdditionalPax}
                            childWithoutBedPrice={childWithoutBedPrice}
                            childWithBed={childWithBed}
                            adultWithBed={adultExtraBedPrice}
                            flagChildWithoutBedIsPercentage={selectedContract?.flag_child_without_bed_is_percentage === true}
                            flagChildWithBedIsPercentage={selectedContract?.flag_child_with_bed_is_percentage === true}
                            flagAdultWithBedIsPercentage={selectedContract?.flag_adult_with_bed_is_percentage === true}
                            onChangeContractFields={(next) => {
                                setSurchargePerAdditionalPax(next.surcharge_per_additional_pax)
                                setChildWithoutBedPrice(next.child_without_bed_price)
                                setChildWithBed(next.child_with_bed)
                                setAdultExtraBedPrice(next.adult_with_bed)
                                if (onFullChange && selectedContract?.contract_id) {
                                    onFullChange(selectedContract.contract_id, next)
                                }
                            }}
                            roomSpecificDifferentSurcharge={roomSpecificDifferentSurcharge}
                            onChangeRoomSpecificDifferentSurcharge={commitRoomSpecificDifferentSurcharge}
                            flagSeasonalExtraBedPricing={showExtraBedBySeason}
                            seasonalExtraBedSeasons={seasons}
                            extraBedRatesBySeason={extraBedRates}
                            extraBedRateIsPercentageBySeason={extraBedRateIsPercentage}
                            onExtraBedRatesChange={handleExtraBedRatesChange}
                            onExtraBedRateIsPercentageChange={handleExtraBedRateIsPercentageChange}
                            readOnly={readOnly}
                        />
                    </Grid>

                    {/* Per-Person Seasonal Surcharges */}
                    {selectedContract?.flag_enable_per_person_seasonal_surcharge === true && (
                        <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                            <PerPersonSeasonalSurcharges
                                seasons={seasons}
                                perPersonSeasonalSurcharge={perPersonSeasonalSurchargeFromContract}
                                readOnly={readOnly}
                                showValidationErrors={showValidationErrors || localValidationErrors}
                                onChange={(next) => {
                                    if (onFullChange && selectedContract?.contract_id) {
                                        onFullChange(selectedContract.contract_id, {
                                            per_person_seasonal_surcharge: next,
                                        })
                                    }
                                }}
                            />
                        </Grid>
                    )}

                    {/* Peak Date Surcharges */}
                    {selectedContract?.flag_enable_peak_date_surcharges === true && (
                        <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                            <RoomSpecificPeakDateSurcharges
                                peakDateSurchargeGroups={selectedContract.peak_date_surcharge_group ?? []}
                                hotelRoomTypes={hotelRoomTypes}
                                readOnly={readOnly}
                                varyByRoomType={selectedContract.flag_peak_surcharge_vary_by_room_type === true}
                                peakDateSurchage={(selectedContract as any)?.peak_date_surchage}
                                onChangePeakDateSurchage={(next) => {
                                    if (onFullChange && selectedContract?.contract_id) {
                                        onFullChange(selectedContract.contract_id, { peak_date_surchage: next } as any)
                                    }
                                }}
                                onChangePeakDateSurchargeGroups={(next) => {
                                    if (onFullChange && selectedContract?.contract_id) {
                                        onFullChange(selectedContract.contract_id, { peak_date_surcharge_group: next })
                                    }
                                }}
                            />
                        </Grid>
                    )}
                    {/* Meal Plan Table */}
                    {/* <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                        <div className={styles.mealPlanSection}>
                            <Table
                                columns={mealPlanColumns}
                                data={mealPlans}
                                keyExtractor={(_row, index) => `meal_plan_${index}`}
                                emptyMessage="No meal plans added"
                                containerClassName={styles.mealPlanTableContainer}
                                // className={styles.mealPlanTable}
                                stickyHeader={true}
                                showHeader={true}
                                minWidth={200}
                            />
                            <div className={styles.mealPlanHeader}>
                                {!readOnly && (
                                    <button
                                        type="button"
                                        className={styles.addBtn}
                                        onClick={() => {
                                            const next: MealPlanRowLocal[] = [
                                                ...mealPlans,
                                                emptyMealPlanRow(),
                                            ]
                                            commitMealPlans(next)
                                        }}
                                    >
                                        + Meal Plan
                                    </button>
                                )}
                            </div>
                        </div>
                    </Grid> */}
                    
                </Grid>
            </div>
            <ContractSeasonDatesBar
                contract={selectedContract}
                contractIndex={contracts.findIndex((c) => c.contract_id === selectedContract?.contract_id)}
            />
        </>
    )
}

// Validate that all prices in the provided array are valid (non-null, non-negative)
export const validateHotelRates = (prices: HotelRoomPricingModel[]): boolean => {
    // If no prices, it's valid (rates might not be configured yet)
    if (!prices || prices.length === 0) return true

    // If there are prices, validate each one
    return prices.every((price) => {
        // Price must be a valid non-negative number
        return price.price !== null && price.price !== undefined && price.price >= 0
    })
}

// Validate that rates are complete for all room type + season combinations
export const validateHotelRatesComplete = (prices: HotelRoomPricingModel[], roomTypeIds: string[], seasonIds: string[]): boolean => {
    if (roomTypeIds.length === 0 || seasonIds.length === 0) return true

    // Check that every room type has a price for every season
    for (const roomTypeId of roomTypeIds) {
        for (const seasonId of seasonIds) {
            const priceEntry = prices.find((p) => p.hotel_room_type_id === roomTypeId && p.season_id === seasonId)
            if (!priceEntry || priceEntry.price === null || priceEntry.price === undefined) {
                return false
            }
        }
    }

    return true
}

export default HotelRates
